import React, { useEffect, useState, useRef } from "react";
import { useNavigate, NavLink, Outlet, Routes, Route, Navigate } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import { api } from "../../lib/api";
import { toast, Toaster } from "sonner";
import { Calendar as CalIcon, EnvelopeSimple, Quotes, Article, SignOut, House, Image as ImageIcon, Plus, PencilSimple, MagnifyingGlass, DownloadSimple, UsersThree, Flower, Receipt, ChartPieSlice, Package, UploadSimple, X } from "@phosphor-icons/react";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../../components/ui/dialog";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import AdminMedia from "./Media";
import AdminTeam from "./Team";
import AdminTreatments from "./Treatments";
import AdminBills from "./Bills";
import Overview from "./Overview";
import Stock from "./Stock";
import { useTreatments } from "../../lib/treatments";
import Pagination from "../../components/Pagination";

const TODAY_ISO = () => new Date().toISOString().slice(0, 10);
const SOURCE_LABEL = { "online": "Online", "walk-in": "Walk-in", "phone": "Phone", "whatsapp": "WhatsApp" };
const SOURCE_BADGE = { "online": "bg-sand text-forest", "walk-in": "bg-terracotta text-cream", "phone": "bg-sage text-cream", "whatsapp": "bg-[#25D366]/90 text-white" };

const MONTH_NAMES = ["January","February","March","April","May","June","July","August","September","October","November","December"];

function monthKey(dateStr) {
  if (!dateStr || dateStr.length < 7) return "";
  return dateStr.slice(0, 7); // YYYY-MM
}
function monthLabel(key) {
  if (!key || key.length < 7) return "—";
  const [y, m] = key.split("-");
  const idx = parseInt(m, 10) - 1;
  return `${MONTH_NAMES[idx] || m} ${y}`;
}

function BookingDialog({ trigger, initial, onSaved }) {
  const editing = Boolean(initial?.id);
  const { items: treatments } = useTreatments();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    preferred_date: TODAY_ISO(), preferred_time: "",
    concern: "", message: "", notes: "",
    source: "walk-in", status: "confirmed",
    follow_up_date: "", follow_up_notes: "", medicine: "",
  });

  useEffect(() => {
    if (open) {
      setForm(initial ? {
        name: initial.name || "", email: initial.email || "", phone: initial.phone || "",
        preferred_date: initial.preferred_date || TODAY_ISO(), preferred_time: initial.preferred_time || "",
        concern: initial.concern || "", message: initial.message || "", notes: initial.notes || "",
        source: initial.source || "walk-in", status: initial.status || "confirmed",
        follow_up_date: initial.follow_up_date || "", follow_up_notes: initial.follow_up_notes || "", medicine: initial.medicine || "",
      } : {
        name: "", email: "", phone: "",
        preferred_date: TODAY_ISO(), preferred_time: "",
        concern: "", message: "", notes: "",
        source: "walk-in", status: "confirmed",
        follow_up_date: "", follow_up_notes: "", medicine: "",
      });
    }
  }, [open, initial]);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e?.target ? e.target.value : e }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.concern || !form.preferred_date || !form.preferred_time) {
      toast.error("Name, phone, date, time and concern are required");
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.patch(`/bookings/${initial.id}`, form);
        toast.success("Visit updated");
      } else {
        await api.post("/bookings/admin", form);
        toast.success("Visit added");
      }
      setOpen(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-2xl bg-cream max-h-[90vh] overflow-y-auto" data-testid="booking-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl">{editing ? "Edit visit" : "Add a new visit"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="booking-dialog-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><Label>Full name *</Label><Input data-testid="bd-name" value={form.name} onChange={set("name")} className="bg-ivory h-11 mt-1" required/></div>
            <div><Label>Phone *</Label><Input data-testid="bd-phone" value={form.phone} onChange={set("phone")} className="bg-ivory h-11 mt-1" required/></div>
            <div className="sm:col-span-2"><Label>Email <span className="text-muted-foreground">(optional)</span></Label><Input data-testid="bd-email" type="email" value={form.email} onChange={set("email")} className="bg-ivory h-11 mt-1"/></div>
            <div><Label>Visit date *</Label><Input data-testid="bd-date" type="date" value={form.preferred_date} onChange={set("preferred_date")} className="bg-ivory h-11 mt-1" required/></div>
            <div><Label>Visit time *</Label><Input data-testid="bd-time" placeholder="10:00 AM" value={form.preferred_time} onChange={set("preferred_time")} className="bg-ivory h-11 mt-1" required/></div>
            <div>
              <Label>Source</Label>
              <Select value={form.source} onValueChange={(v) => set("source")(v)}>
                <SelectTrigger data-testid="bd-source" className="bg-ivory h-11 mt-1"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="walk-in">Walk-in</SelectItem>
                  <SelectItem value="phone">Phone</SelectItem>
                  <SelectItem value="whatsapp">WhatsApp</SelectItem>
                  <SelectItem value="online">Online</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => set("status")(v)}>
                <SelectTrigger data-testid="bd-status" className="bg-ivory h-11 mt-1"><SelectValue/></SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="confirmed">Confirmed</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2">
              <Label>Concern *</Label>
              <Select value={form.concern} onValueChange={(v) => set("concern")(v)}>
                <SelectTrigger data-testid="bd-concern" className="bg-ivory h-11 mt-1"><SelectValue placeholder="Select a treatment / concern"/></SelectTrigger>
                <SelectContent>
                  {treatments.map(t => <SelectItem key={t.id || t.slug} value={t.title}>{t.title}</SelectItem>)}
                  <SelectItem value="General wellness consultation">General wellness consultation</SelectItem>
                  <SelectItem value="Follow-up visit">Follow-up visit</SelectItem>
                  <SelectItem value="Other">Other (custom)</SelectItem>
                </SelectContent>
              </Select>
              {form.concern === "Other" && (
                <Input
                  data-testid="bd-concern-custom"
                  className="bg-ivory h-11 mt-2"
                  placeholder="Type the custom concern"
                  onChange={(e) => set("concern")(e.target.value || "Other")}
                />
              )}
              {editing && form.concern && !treatments.some(t => t.title === form.concern) && !["General wellness consultation","Follow-up visit","Other"].includes(form.concern) && (
                <p className="text-xs text-muted-foreground mt-1">Current value: <b>{form.concern}</b> (keep or pick a new one)</p>
              )}
            </div>
            <div className="sm:col-span-2"><Label>Patient message / brief</Label><Textarea data-testid="bd-message" value={form.message} onChange={set("message")} className="bg-ivory mt-1 min-h-[70px]" placeholder="What did the patient share?"/></div>
            <div className="sm:col-span-2"><Label>Private notes (CRM)</Label><Textarea data-testid="bd-notes" value={form.notes} onChange={set("notes")} className="bg-ivory mt-1 min-h-[90px]" placeholder="Diagnosis, prescription, follow-up reminders. Visible only in admin."/></div>
            <div><Label>Follow-up date</Label><Input data-testid="bd-followup-date" type="date" value={form.follow_up_date} onChange={set("follow_up_date")} className="bg-ivory h-11 mt-1"/></div>
            <div><Label>Medicine purchased</Label><Input data-testid="bd-medicine" value={form.medicine} onChange={set("medicine")} className="bg-ivory h-11 mt-1" placeholder="e.g., Ashwagandha Churna 100g"/></div>
            <div className="sm:col-span-2"><Label>Follow-up notes</Label><Textarea data-testid="bd-followup-notes" value={form.follow_up_notes} onChange={set("follow_up_notes")} className="bg-ivory mt-1 min-h-[70px]" placeholder="What did the patient say on the follow-up call?"/></div>
          </div>
          <DialogFooter className="gap-2">
            <button type="button" onClick={() => setOpen(false)} className="rounded-full border border-border px-5 py-2 text-sm">Cancel</button>
            <button type="submit" disabled={saving} data-testid="bd-submit" className="rounded-full bg-forest text-cream px-6 py-2.5 text-sm hover:bg-terracotta transition-all disabled:opacity-50">
              {saving ? "Saving…" : (editing ? "Save changes" : "Add visit")}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function FollowUpDialog({ trigger, booking, onSaved }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ follow_up_date: "", follow_up_notes: "", medicine: "" });

  useEffect(() => {
    if (open && booking) {
      setForm({
        follow_up_date: booking.follow_up_date || "",
        follow_up_notes: booking.follow_up_notes || "",
        medicine: booking.medicine || "",
      });
    }
  }, [open, booking]);

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/bookings/${booking.id}`, form);
      toast.success("Follow-up saved");
      setOpen(false);
      onSaved?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-lg bg-cream" data-testid="followup-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl">Follow-up — {booking?.name}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="followup-form">
          <div className="text-xs text-muted-foreground bg-ivory rounded-xl p-3 border border-border/50">
            <div><b>Phone:</b> {booking?.phone}</div>
            {booking?.email && <div><b>Email:</b> {booking.email}</div>}
            <div><b>Concern:</b> {booking?.concern}</div>
            <div><b>Visit:</b> {booking?.preferred_date} at {booking?.preferred_time}</div>
          </div>
          <div><Label>Next follow-up date</Label><Input data-testid="fu-date" type="date" value={form.follow_up_date} onChange={set("follow_up_date")} className="bg-ivory h-11 mt-1"/></div>
          <div><Label>Medicine purchased</Label><Input data-testid="fu-medicine" value={form.medicine} onChange={set("medicine")} className="bg-ivory h-11 mt-1" placeholder="e.g., Ashwagandha Churna 100g, Chyawanprash"/></div>
          <div><Label>Follow-up notes</Label><Textarea data-testid="fu-notes" value={form.follow_up_notes} onChange={set("follow_up_notes")} className="bg-ivory mt-1 min-h-[100px]" placeholder="What the patient said on the call — symptoms, response, next steps."/></div>
          <DialogFooter>
            <button type="button" onClick={() => setOpen(false)} className="rounded-full border border-border px-5 py-2 text-sm">Cancel</button>
            <button type="submit" disabled={saving} data-testid="fu-submit" className="rounded-full bg-forest text-cream px-6 py-2.5 text-sm hover:bg-terracotta disabled:opacity-50">
              {saving ? "Saving…" : "Save follow-up"}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function Bookings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [monthFilter, setMonthFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(15);
  const load = () => api.get("/bookings").then(r => setRows(r.data));
  useEffect(() => { load(); }, []);
  const setStatus = async (id, status) => {
    try { await api.patch(`/bookings/${id}`, { status }); toast.success("Updated"); load(); }
    catch { toast.error("Failed"); }
  };
  const remove = async (id) => { if (!window.confirm("Delete this visit?")) return; await api.delete(`/bookings/${id}`); toast.success("Deleted"); load(); };

  const availableMonths = React.useMemo(() => {
    const set = new Set(rows.map(r => monthKey(r.preferred_date)).filter(Boolean));
    return Array.from(set).sort().reverse();
  }, [rows]);

  const filtered = rows.filter(r => {
    if (sourceFilter !== "all" && r.source !== sourceFilter) return false;
    if (monthFilter !== "all" && monthKey(r.preferred_date) !== monthFilter) return false;
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return [r.name, r.email, r.phone, r.concern, r.notes, r.message].some(v => (v || "").toLowerCase().includes(q));
  });

  // Reset to first page when filters change
  useEffect(() => { setPage(1); }, [query, sourceFilter, monthFilter, pageSize]);
  const pageRows = filtered.slice((page - 1) * pageSize, page * pageSize);

  const stats = {
    total: rows.length,
    walkIns: rows.filter(r => r.source === "walk-in").length,
    online: rows.filter(r => r.source === "online").length,
    pending: rows.filter(r => r.status === "pending").length,
  };

  const exportPdf = () => {
    if (filtered.length === 0) { toast.error("No visits to export with current filters"); return; }
    const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
    const pageWidth = doc.internal.pageSize.getWidth();

    // Header
    doc.setFont("helvetica", "bold");
    doc.setFontSize(18);
    doc.setTextColor(15, 59, 32); // forest
    doc.text("Yuva Ayurvedic Clinic Private Limited", 40, 40);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.setTextColor(92, 107, 97);
    doc.text("Prayagraj, Uttar Pradesh · info@yuvaayurvedic.com · +91 95190 78666", 40, 56);

    // Title
    doc.setFont("helvetica", "bold");
    doc.setFontSize(13);
    doc.setTextColor(15, 59, 32);
    const title = monthFilter === "all" ? "Patient Visits — All months" : `Patient Visits — ${monthLabel(monthFilter)}`;
    doc.text(title, 40, 86);

    // Meta line
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9);
    doc.setTextColor(92, 107, 97);
    const parts = [
      `Generated: ${new Date().toLocaleString()}`,
      `Records: ${filtered.length}`,
    ];
    if (sourceFilter !== "all") parts.push(`Source: ${SOURCE_LABEL[sourceFilter] || sourceFilter}`);
    if (query.trim()) parts.push(`Search: "${query.trim()}"`);
    doc.text(parts.join("  ·  "), 40, 102);

    // Table
    autoTable(doc, {
      startY: 120,
      head: [["Date", "Time", "Month", "Name", "Phone", "Email", "Concern", "Source", "Status", "Follow-up", "Medicine", "Follow-up notes", "Notes"]],
      body: filtered.map(r => [
        r.preferred_date || "",
        r.preferred_time || "",
        monthLabel(monthKey(r.preferred_date)),
        r.name || "",
        r.phone || "",
        r.email || "",
        r.concern || "",
        SOURCE_LABEL[r.source] || r.source || "",
        (r.status || "").toUpperCase(),
        r.follow_up_date || "",
        r.medicine || "",
        r.follow_up_notes || "",
        [r.message, r.notes].filter(Boolean).join(" | "),
      ]),
      styles: { font: "helvetica", fontSize: 7, cellPadding: 4, textColor: [30, 30, 30], lineColor: [216, 210, 196], lineWidth: 0.3 },
      headStyles: { fillColor: [15, 59, 32], textColor: [247, 245, 240], fontStyle: "bold", fontSize: 7 },
      alternateRowStyles: { fillColor: [247, 245, 240] },
      columnStyles: {
        0: { cellWidth: 52 }, 1: { cellWidth: 48 }, 2: { cellWidth: 60 },
        3: { cellWidth: 70 }, 4: { cellWidth: 65 }, 5: { cellWidth: 80 },
        6: { cellWidth: 75 }, 7: { cellWidth: 45 }, 8: { cellWidth: 50 },
        9: { cellWidth: 55 }, 10: { cellWidth: 70 }, 11: { cellWidth: 75 },
        12: { cellWidth: "auto" },
      },
      didDrawPage: (data) => {
        const pageCount = doc.internal.getNumberOfPages();
        const pageNo = data.pageNumber;
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);
        doc.text(`Page ${pageNo} of ${pageCount}  ·  Yuva Ayurvedic Clinic Private Limited CRM`, pageWidth - 40, doc.internal.pageSize.getHeight() - 20, { align: "right" });
      },
      margin: { left: 40, right: 40 },
    });

    const filename = `yuva-visits-${monthFilter === "all" ? "all" : monthFilter}-${new Date().toISOString().slice(0,10)}.pdf`;
    doc.save(filename);
    toast.success(`Exported ${filtered.length} visits`);
  };

  return (
    <div data-testid="admin-bookings">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
        <div>
          <h1 className="font-serif-display text-4xl">Visits & Bookings</h1>
          <p className="text-muted-foreground mt-2">Your clinic's simple CRM — add walk-ins, call-ins, or online bookings.</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={exportPdf}
            data-testid="export-pdf-btn"
            className="inline-flex items-center gap-2 rounded-full border border-forest text-forest px-5 py-2.5 text-sm hover:bg-forest hover:text-cream transition-all"
          >
            <DownloadSimple size={16} weight="bold"/> Export PDF
          </button>
          {isAdmin && (
            <BookingDialog
              onSaved={load}
              trigger={
                <button data-testid="add-visit-btn" className="inline-flex items-center gap-2 rounded-full bg-forest text-cream px-5 py-2.5 text-sm hover:bg-terracotta transition-all">
                  <Plus size={16} weight="bold"/> Add visit
                </button>
              }
            />
          )}
        </div>
      </div>

      <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: "Total visits", value: stats.total },
          { label: "Walk-ins", value: stats.walkIns },
          { label: "Online", value: stats.online },
          { label: "Pending", value: stats.pending },
        ].map(s => (
          <div key={s.label} className="bg-ivory border border-border/50 rounded-2xl p-5">
            <div className="overline">{s.label}</div>
            <div className="font-serif-display text-4xl mt-2">{s.value}</div>
          </div>
        ))}
      </div>

      <div className="mt-6 flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground"/>
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by name, phone, email, concern or notes…"
            className="bg-ivory h-11 pl-11"
            data-testid="bookings-search"
          />
        </div>
        <Select value={monthFilter} onValueChange={setMonthFilter}>
          <SelectTrigger className="w-full md:w-52 bg-ivory h-11" data-testid="bookings-month-filter"><SelectValue placeholder="All months"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All months</SelectItem>
            {availableMonths.map(m => <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={sourceFilter} onValueChange={setSourceFilter}>
          <SelectTrigger className="w-full md:w-48 bg-ivory h-11" data-testid="bookings-source-filter"><SelectValue placeholder="All sources"/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All sources</SelectItem>
            <SelectItem value="walk-in">Walk-in</SelectItem>
            <SelectItem value="online">Online</SelectItem>
            <SelectItem value="phone">Phone</SelectItem>
            <SelectItem value="whatsapp">WhatsApp</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="mt-3 text-xs text-muted-foreground">
        Showing <b className="text-forest">{filtered.length}</b> of {rows.length} visits
        {monthFilter !== "all" && <> · Month: <b className="text-forest">{monthLabel(monthFilter)}</b></>}
      </div>

      <div className="mt-4 bg-ivory border border-border/50 rounded-2xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th className="p-4">Date/Time</th>
              <th className="p-4">Month</th>
              <th className="p-4">Name</th>
              <th className="p-4">Contact</th>
              <th className="p-4">Concern</th>
              <th className="p-4">Follow-up & Medicine</th>
              <th className="p-4">Source</th>
              <th className="p-4">Status</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map(r => (
              <tr key={r.id} className="border-t border-border/40 align-top" data-testid={`booking-${r.id}`}>
                <td className="p-4 whitespace-nowrap">{r.preferred_date}<br/><span className="text-muted-foreground">{r.preferred_time}</span></td>
                <td className="p-4 whitespace-nowrap text-xs text-muted-foreground">{monthLabel(monthKey(r.preferred_date))}</td>
                <td className="p-4 font-medium">{r.name}</td>
                <td className="p-4 text-xs">{r.email && <div>{r.email}</div>}<div className="text-muted-foreground">{r.phone}</div></td>
                <td className="p-4">
                  <div>{r.concern}</div>
                  {r.message && <div className="text-xs text-muted-foreground mt-1 line-clamp-2">{r.message}</div>}
                  {r.notes && <div className="text-xs text-terracotta/90 mt-1 line-clamp-2 italic">🔒 {r.notes}</div>}
                </td>
                <td className="p-4 text-xs">
                  {r.follow_up_date && <div><span className="text-muted-foreground">Next:</span> <b className="text-forest">{r.follow_up_date}</b></div>}
                  {r.medicine && <div className="mt-1"><span className="text-muted-foreground">Rx:</span> {r.medicine}</div>}
                  {r.follow_up_notes && <div className="mt-1 text-muted-foreground line-clamp-2 italic">💬 {r.follow_up_notes}</div>}
                  {!r.follow_up_date && !r.medicine && !r.follow_up_notes && <span className="text-muted-foreground">—</span>}
                </td>
                <td className="p-4"><span className={`inline-block px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.15em] ${SOURCE_BADGE[r.source] || SOURCE_BADGE.online}`}>{SOURCE_LABEL[r.source] || r.source}</span></td>
                <td className="p-4">
                  <Select value={r.status} onValueChange={(v) => setStatus(r.id, v)}>
                    <SelectTrigger className="w-36 h-9 bg-cream"><SelectValue/></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </td>
                <td className="p-4 whitespace-nowrap">
                  <div className="flex items-center gap-3">
                    <FollowUpDialog
                      booking={r}
                      onSaved={load}
                      trigger={
                        <button className="text-xs text-forest hover:text-terracotta inline-flex items-center gap-1" data-testid={`followup-${r.id}`}>
                          <PencilSimple size={14}/> Follow-up
                        </button>
                      }
                    />
                    {isAdmin && (
                      <BookingDialog
                        initial={r}
                        onSaved={load}
                        trigger={
                          <button className="text-xs text-forest hover:text-terracotta inline-flex items-center gap-1" data-testid={`edit-booking-${r.id}`}>
                            Edit all
                          </button>
                        }
                      />
                    )}
                    {isAdmin && (
                      <button onClick={() => remove(r.id)} className="text-xs text-destructive hover:underline" data-testid={`delete-booking-${r.id}`}>Delete</button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={9} className="p-10 text-center text-muted-foreground">{rows.length === 0 ? "No visits yet. Click \"Add visit\" to log one." : "No results match your filter."}</td></tr>}
          </tbody>
        </table>
      </div>
      {filtered.length > 0 && (
        <Pagination
          page={page}
          pageSize={pageSize}
          total={filtered.length}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          testId="bookings-pagination"
        />
      )}
    </div>
  );
}

function AdminShell() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  if (loading) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace/>;

  const isAdmin = user.role === "admin";
  const isFinance = user.role === "finance";
  const isStaff = user.role === "staff";

  const link = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-3 rounded-xl text-sm tracking-wide transition-all ${
      isActive ? "bg-cream text-forest" : "text-cream/80 hover:bg-forest/50 hover:text-cream"
    }`;

  return (
    <div className="min-h-screen bg-cream flex" data-testid="admin-dashboard">
      <Toaster richColors position="top-center"/>
      <aside className="w-64 bg-forest text-cream p-6 flex flex-col sticky top-0 h-screen">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-10 h-10 rounded-full bg-cream flex items-center justify-center">
            <img src="https://customer-assets.emergentagent.com/job_yuva-modern/artifacts/d1s5jglr_Untitled_design__1_-removebg-preview.png" alt="Yuva" className="w-8 h-8 object-contain"/>
          </div>
          <div>
            <div className="font-serif-display text-xl leading-none">Yuva {isAdmin ? "Admin" : isFinance ? "Finance" : "CRM"}</div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-cream/50 mt-1">{user.name} · {isAdmin ? "Admin" : isFinance ? "Finance" : "Staff"}</div>
          </div>
        </div>
        <nav className="space-y-1 flex-1">
          {isAdmin && <NavLink to="/admin" end className={link} data-testid="nav-overview"><ChartPieSlice size={18}/> Overview</NavLink>}
          {(isAdmin || isStaff) && <NavLink to="/admin/bookings" className={link} data-testid="nav-bookings"><CalIcon size={18}/> Visits & Bookings</NavLink>}
          {(isAdmin || isFinance) && <NavLink to="/admin/bills" className={link} data-testid="nav-bills"><Receipt size={18}/> Bills of Entry</NavLink>}
          {(isAdmin || isStaff) && <NavLink to="/admin/stock" className={link} data-testid="nav-stock"><Package size={18}/> Stock</NavLink>}
          {isAdmin && <NavLink to="/admin/contacts" className={link} data-testid="nav-contacts"><EnvelopeSimple size={18}/> Contacts</NavLink>}
          {isAdmin && <NavLink to="/admin/treatments" className={link} data-testid="nav-treatments"><Flower size={18}/> Treatments</NavLink>}
          {isAdmin && <NavLink to="/admin/testimonials" className={link} data-testid="nav-testimonials"><Quotes size={18}/> Testimonials</NavLink>}
          {isAdmin && <NavLink to="/admin/blog" className={link} data-testid="nav-blog"><Article size={18}/> Journal</NavLink>}
          {isAdmin && <NavLink to="/admin/media" className={link} data-testid="nav-media"><ImageIcon size={18}/> Site Content</NavLink>}
          {isAdmin && <NavLink to="/admin/team" className={link} data-testid="nav-team"><UsersThree size={18}/> Team access</NavLink>}
        </nav>
        <div className="space-y-1 mt-6">
          <button onClick={() => nav("/")} className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-cream/70 hover:bg-forest/50 hover:text-cream"><House size={18}/> View site</button>
          <button onClick={() => { logout(); nav("/admin/login"); }} data-testid="admin-logout" className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-sm text-cream/70 hover:bg-forest/50 hover:text-cream"><SignOut size={18}/> Logout</button>
        </div>
      </aside>
      <main className="flex-1 p-8 lg:p-12 overflow-x-hidden">
        <Outlet/>
      </main>
    </div>
  );
}

function RequireAdmin({ children }) {
  const { user } = useAuth();
  if (user?.role !== "admin") return <div className="p-12 text-center text-muted-foreground" data-testid="forbidden">This section is admin-only.</div>;
  return children;
}

function RequireCRM({ children }) {
  const { user } = useAuth();
  if (user?.role === "finance") return <Navigate to="/admin/bills" replace/>;
  return children;
}

function AdminLanding() {
  const { user } = useAuth();
  if (user?.role === "finance") return <Navigate to="/admin/bills" replace/>;
  if (user?.role === "staff") return <Navigate to="/admin/bookings" replace/>;
  // admin
  return <Overview/>;
}

function RequireBills({ children }) {
  const { user } = useAuth();
  if (user?.role !== "admin" && user?.role !== "finance") {
    return <div className="p-12 text-center text-muted-foreground" data-testid="forbidden">Bills of Entry are visible only to Admin and Finance roles.</div>;
  }
  return children;
}

function Contacts() {
  const [rows, setRows] = useState([]);
  const [query, setQuery] = useState("");
  const [sourceFilter, setSourceFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const load = () => api.get("/contacts").then(r => setRows(r.data));
  useEffect(() => { load(); }, []);
  const remove = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/contacts/${id}`); toast.success("Deleted"); load(); };

  const filtered = rows.filter(c => {
    if (sourceFilter !== "all" && (c.source || "enquiry") !== sourceFilter) return false;
    if (!query.trim()) return true;
    const q = query.toLowerCase();
    return [c.name, c.email, c.phone, c.subject, c.message, c.treatment].some(v => (v || "").toLowerCase().includes(q));
  });

  // Reset to first page when filters change
  useEffect(() => { setPage(1); }, [query, sourceFilter, pageSize]);
  const pageRows = filtered.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div data-testid="admin-contacts">
      <h1 className="font-serif-display text-4xl">Contacts</h1>
      <p className="text-muted-foreground mt-2">Every enquiry, booking and walk-in in one place — your customer database.</p>

      <div className="mt-6 flex flex-col md:flex-row gap-3">
        <div className="relative flex-1">
          <MagnifyingGlass size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-muted-foreground"/>
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, phone, email or treatment…" className="bg-ivory h-11 pl-11" data-testid="contacts-search"/>
        </div>
        <Select value={sourceFilter} onValueChange={setSourceFilter}>
          <SelectTrigger className="w-full md:w-48 bg-ivory h-11" data-testid="contacts-source-filter"><SelectValue/></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All contacts</SelectItem>
            <SelectItem value="booking">From bookings</SelectItem>
            <SelectItem value="enquiry">From enquiries</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <div className="mt-3 text-xs text-muted-foreground">
        Showing <b className="text-forest">{filtered.length}</b> of {rows.length} contacts
      </div>

      <div className="mt-4 bg-ivory border border-border/50 rounded-2xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th className="p-4">Name</th>
              <th className="p-4">Email</th>
              <th className="p-4">Phone</th>
              <th className="p-4">Visit date</th>
              <th className="p-4">Treatment</th>
              <th className="p-4">Source</th>
              <th className="p-4"></th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map(c => (
              <tr key={c.id} className="border-t border-border/40 align-top" data-testid={`contact-${c.id}`}>
                <td className="p-4">
                  <div className="font-medium">{c.name}</div>
                  <div className="text-[10px] font-mono text-muted-foreground mt-1">ID: {c.id.slice(0, 8)}…</div>
                </td>
                <td className="p-4 text-xs">{c.email || <span className="text-muted-foreground">—</span>}</td>
                <td className="p-4 text-xs">{c.phone || <span className="text-muted-foreground">—</span>}</td>
                <td className="p-4 text-xs whitespace-nowrap">{c.visit_date || <span className="text-muted-foreground">—</span>}</td>
                <td className="p-4 text-xs">{c.treatment || c.subject || <span className="text-muted-foreground">—</span>}</td>
                <td className="p-4"><span className={`inline-block px-2.5 py-1 rounded-full text-[10px] uppercase tracking-[0.15em] ${(c.source || 'enquiry') === 'booking' ? 'bg-terracotta text-cream' : 'bg-sand text-forest'}`}>{c.source === "booking" ? "Booking" : "Enquiry"}</span></td>
                <td className="p-4"><button onClick={() => remove(c.id)} className="text-xs text-destructive hover:underline" data-testid={`delete-contact-${c.id}`}>Delete</button></td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={7} className="p-10 text-center text-muted-foreground">{rows.length === 0 ? "No contacts yet." : "No results match your filter."}</td></tr>}
          </tbody>
        </table>
      </div>
      {filtered.length > 0 && (
        <Pagination
          page={page}
          pageSize={pageSize}
          total={filtered.length}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
          testId="contacts-pagination"
        />
      )}

      <details className="mt-8">
        <summary className="cursor-pointer text-sm text-muted-foreground hover:text-forest">Show full messages (from enquiry form submissions)</summary>
        <div className="mt-4 grid gap-3">
          {filtered.filter(c => c.source !== "booking" && c.message).map(c => (
            <div key={c.id} className="bg-ivory border border-border/50 rounded-xl p-5 text-sm">
              <div className="font-medium">{c.name} <span className="text-muted-foreground font-normal">· {c.email}</span></div>
              <div className="text-xs uppercase tracking-[0.2em] text-terracotta mt-1">{c.subject}</div>
              <p className="mt-3 whitespace-pre-wrap text-foreground/85">{c.message}</p>
              <div className="text-xs text-muted-foreground mt-2">{new Date(c.created_at).toLocaleString()}</div>
            </div>
          ))}
        </div>
      </details>
    </div>
  );
}

function Testimonials() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ author: "", location: "", quote: "", rating: 5, published: true });
  const load = () => api.get("/testimonials", { params: { all: true } }).then(r => setRows(r.data));
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => { e.preventDefault(); await api.post("/testimonials", { ...form, rating: Number(form.rating) }); toast.success("Added"); setForm({ author:"", location:"", quote:"", rating:5, published:true }); load(); };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/testimonials/${id}`); load(); };
  return (
    <div data-testid="admin-testimonials">
      <h1 className="font-serif-display text-4xl">Testimonials</h1>
      <div className="grid lg:grid-cols-2 gap-8 mt-8">
        <form onSubmit={submit} className="bg-ivory border border-border/50 rounded-2xl p-6 space-y-4" data-testid="testimonial-form">
          <h3 className="font-serif-display text-2xl">Add new</h3>
          <div><Label>Author</Label><Input className="bg-cream h-11 mt-1" value={form.author} onChange={set("author")} required/></div>
          <div><Label>Location</Label><Input className="bg-cream h-11 mt-1" value={form.location} onChange={set("location")}/></div>
          <div><Label>Quote</Label><Textarea className="bg-cream mt-1" value={form.quote} onChange={set("quote")} required/></div>
          <div><Label>Rating (1-5)</Label><Input type="number" min={1} max={5} className="bg-cream h-11 mt-1" value={form.rating} onChange={set("rating")}/></div>
          <button className="rounded-full bg-forest text-cream px-6 py-3" data-testid="testimonial-add">Add testimonial</button>
        </form>
        <div className="space-y-3">
          {rows.map(r => (
            <div key={r.id} className="bg-ivory border border-border/50 rounded-xl p-5" data-testid={`testimonial-row-${r.id}`}>
              <div className="flex items-start justify-between">
                <div><div className="font-medium">{r.author}</div><div className="text-xs text-muted-foreground">{r.location} · {"★".repeat(r.rating)}</div></div>
                <button onClick={() => del(r.id)} className="text-xs text-destructive hover:underline">Delete</button>
              </div>
              <p className="mt-3 text-sm italic">"{r.quote}"</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function BlogAdmin() {
  const [rows, setRows] = useState([]);
  const [form, setForm] = useState({ title:"", excerpt:"", content:"", category:"Wellness", cover_image:"", published:true, title_hi:"", excerpt_hi:"", content_hi:"" });
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const load = () => api.get("/blog", { params: { all: true } }).then(r => setRows(r.data));
  useEffect(() => { load(); }, []);
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const onFilePick = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please select an image file"); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error("Max 5 MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm(f => ({ ...f, cover_image: data.url }));
      toast.success("Cover image uploaded");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const submit = async (e) => { e.preventDefault(); await api.post("/blog", { ...form, author: "Yuva Ayurvedic Clinic Private Limited" }); toast.success("Published"); setForm({ title:"", excerpt:"", content:"", category:"Wellness", cover_image:"", published:true, title_hi:"", excerpt_hi:"", content_hi:"" }); load(); };
  const del = async (id) => { if (!window.confirm("Delete?")) return; await api.delete(`/blog/${id}`); load(); };
  return (
    <div data-testid="admin-blog">
      <h1 className="font-serif-display text-4xl">Journal</h1>
      <div className="grid lg:grid-cols-2 gap-8 mt-8">
        <form onSubmit={submit} className="bg-ivory border border-border/50 rounded-2xl p-6 space-y-4" data-testid="blog-form">
          <h3 className="font-serif-display text-2xl">New article</h3>
          <div><Label>Title</Label><Input className="bg-cream h-11 mt-1" value={form.title} onChange={set("title")} required/></div>
          <div><Label>Category</Label><Input className="bg-cream h-11 mt-1" value={form.category} onChange={set("category")}/></div>

          <div>
            <Label>Cover image</Label>
            <Input
              data-testid="blog-cover-url"
              className="bg-cream h-11 mt-1"
              value={form.cover_image}
              onChange={set("cover_image")}
              placeholder="Paste a URL or upload from your computer"
            />
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={onFilePick}
              className="hidden"
              data-testid="blog-cover-file"
            />
            <div className="flex items-center gap-2 mt-2">
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid="blog-cover-upload"
                className="inline-flex items-center gap-2 rounded-full border border-border px-4 py-1.5 text-xs text-forest hover:bg-forest hover:text-cream transition-all disabled:opacity-50"
              >
                <UploadSimple size={14}/> {uploading ? "Uploading…" : "Upload from computer"}
              </button>
              {form.cover_image && (
                <button
                  type="button"
                  onClick={() => setForm(f => ({ ...f, cover_image: "" }))}
                  data-testid="blog-cover-clear"
                  className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-destructive"
                >
                  <X size={12}/> Clear
                </button>
              )}
            </div>
            {form.cover_image && (
              <div className="mt-3">
                <img
                  src={form.cover_image}
                  alt="Cover preview"
                  className="w-40 h-24 object-cover rounded-lg border border-border/60"
                  data-testid="blog-cover-preview"
                  onError={(e) => { e.currentTarget.style.opacity = "0.3"; }}
                />
              </div>
            )}
            <p className="text-[10px] text-muted-foreground mt-1.5">Max 5 MB. Accepts JPG, PNG, WebP.</p>
          </div>

          <div><Label>Excerpt</Label><Textarea className="bg-cream mt-1" value={form.excerpt} onChange={set("excerpt")} required/></div>
          <div><Label>Content (HTML)</Label><Textarea className="bg-cream mt-1 min-h-[200px] font-mono text-xs" value={form.content} onChange={set("content")} required/></div>

          <div className="bg-sand/40 border border-border/40 rounded-xl p-4 space-y-3">
            <div className="flex items-center gap-2">
              <span className="text-xs uppercase tracking-[0.22em] text-forest font-medium">हिंदी</span>
              <span className="text-xs text-muted-foreground">— optional. Shown when site is in Hindi.</span>
            </div>
            <div><Label>शीर्षक (Title — Hindi)</Label><Input data-testid="blog-title-hi" className="bg-cream h-11 mt-1" value={form.title_hi} onChange={set("title_hi")}/></div>
            <div><Label>सारांश (Excerpt — Hindi)</Label><Textarea data-testid="blog-excerpt-hi" className="bg-cream mt-1 min-h-[70px]" value={form.excerpt_hi} onChange={set("excerpt_hi")}/></div>
            <div><Label>विषय-वस्तु (Content HTML — Hindi)</Label><Textarea data-testid="blog-content-hi" className="bg-cream mt-1 min-h-[160px] font-mono text-xs" value={form.content_hi} onChange={set("content_hi")}/></div>
          </div>

          <button disabled={uploading} className="rounded-full bg-forest text-cream px-6 py-3 disabled:opacity-50" data-testid="blog-publish">Publish</button>
        </form>
        <div className="space-y-3">
          {rows.map(r => (
            <div key={r.id} className="bg-ivory border border-border/50 rounded-xl p-5 flex gap-4" data-testid={`blog-row-${r.id}`}>
              {r.cover_image && <img src={r.cover_image} className="w-20 h-20 object-cover rounded-lg"/>}
              <div className="flex-1">
                <div className="font-serif-display text-xl">{r.title}</div>
                <div className="text-xs text-muted-foreground mt-1">{r.category} · {new Date(r.created_at).toLocaleDateString()}</div>
                <p className="mt-2 text-sm text-muted-foreground line-clamp-2">{r.excerpt}</p>
              </div>
              <button onClick={() => del(r.id)} className="text-xs text-destructive self-start hover:underline">Delete</button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  return (
    <Routes>
      <Route element={<AdminShell/>}>
        <Route index element={<AdminLanding/>}/>
        <Route path="bookings" element={<RequireCRM><Bookings/></RequireCRM>}/>
        <Route path="bills" element={<RequireBills><AdminBills/></RequireBills>}/>
        <Route path="stock" element={<RequireCRM><Stock/></RequireCRM>}/>
        <Route path="contacts" element={<RequireAdmin><Contacts/></RequireAdmin>}/>
        <Route path="treatments" element={<RequireAdmin><AdminTreatments/></RequireAdmin>}/>
        <Route path="testimonials" element={<RequireAdmin><Testimonials/></RequireAdmin>}/>
        <Route path="blog" element={<RequireAdmin><BlogAdmin/></RequireAdmin>}/>
        <Route path="media" element={<RequireAdmin><AdminMedia/></RequireAdmin>}/>
        <Route path="team" element={<RequireAdmin><AdminTeam/></RequireAdmin>}/>
      </Route>
    </Routes>
  );
}
